import { Injectable } from '@angular/core'
import { LogService } from 'htv-lib'
import { TizenPaletteConfig, TizenPaletteConfigPlayMode } from '../interfaces/htv-config'
import { safeStringify } from '../utils/safe-stringify'
import {
  TizenPaletteDisplayOption,
  TizenPaletteMotionDetector,
  TizenPaletteMotionSleepTime,
  TizenPaletteNightMode,
  TizenPalettePlayMode,
  TizenPalettePowerOffInfo,
  TizenPalettePowerOffMode,
  TizenPaletteRotationTime,
  TizenPaletteScheduleInfo,
} from './tizen-types'

declare const tizen: any | undefined
declare const webapis: any | undefined

@Injectable({ providedIn: 'root' })
export class TizenPalette {
  private readonly PALETTE_IMAGES_PATH = 'wgt-private/palette'

  constructor(private logService: LogService) {
    // do nothing
  }

  getVersion(): string {
    try {
      return webapis.palette.getVersion()
    } catch (err: any) {
      this.logService.error(`[webapis.palette.getVersion()] [${err?.code}] ${err?.message}`)
      return 'Unsupported'
    }
  }

  setPowerOffToPaletteMode(info: TizenPalettePowerOffInfo): boolean {
    try {
      webapis.palette.setPowerOffToPaletteMode(info)
      return true
    } catch (err: any) {
      this.logService.error(
        `[webapis.palette.setPowerOffToPaletteMode(${JSON.stringify(info)})] [${err?.code}] ${err?.message}`
      )
      return false
    }
  }

  getPowerOffToPaletteMode(): any {
    try {
      return webapis.palette.getPowerOffToPaletteMode()
    } catch (err: any) {
      this.logService.error(`[webapis.palette.getPowerOffToPaletteMode()] [${err?.code}] ${err?.message}`)
      return 'Unsupported'
    }
  }

  setPlayMode(mode: TizenPaletteConfigPlayMode): boolean {
    const playMode =
      mode === TizenPaletteConfigPlayMode.GEN_WALLPAPER
        ? TizenPalettePlayMode.GEN_WALLPAPER
        : TizenPalettePlayMode.EXTERNAL_CONNECTION
    try {
      webapis.palette.setPlayMode(playMode)
      return true
    } catch (err: any) {
      this.logService.error(`[webapis.palette.setPlayMode(${mode})] [${err?.code}] ${err?.message}`)
      return false
    }
  }

  getPlayMode(): string {
    try {
      return webapis.palette.getPlayMode()
    } catch (err: any) {
      this.logService.error(`[webapis.palette.getPlayMode()] [${err?.code}] ${err?.message}`)
      return 'Unsupported'
    }
  }

  setSchedule(scheduleInfo: TizenPaletteScheduleInfo): boolean {
    try {
      webapis.palette.setSchedule(scheduleInfo)
      return true
    } catch (err: any) {
      this.logService.error(
        `[webapis.palette.setSchedule(${JSON.stringify(scheduleInfo)})] [${err?.code}] ${err?.message}`
      )
      return false
    }
  }

  getSchedule(): TizenPaletteScheduleInfo | null {
    try {
      return webapis.palette.getSchedule()
    } catch (err: any) {
      this.logService.error(`[webapis.palette.getSchedule()] [${err?.code}] ${err?.message}`)
      return null
    }
  }

  deleteAllImages(): boolean {
    try {
      webapis.palette.deleteAllImage()
      return true
    } catch (err: any) {
      this.logService.error(`[webapis.palette.deleteAllImage()] [${err?.code}] ${err?.message}`)
      return false
    }
  }

  setRotationTime(time: TizenPaletteRotationTime): boolean {
    try {
      webapis.palette.setRotationTime(time)
      return true
    } catch (err: any) {
      this.logService.error(`[webapis.palette.setRotationTime(${time})] [${err?.code}] ${err?.message}`)
      return false
    }
  }

  getRotationTime(): string {
    try {
      return webapis.palette.getRotationTime()
    } catch (err: any) {
      this.logService.error(`[webapis.palette.getRotationTime()] [${err?.code}] ${err?.message}`)
      return 'Unsupported'
    }
  }

  setDisplayOption(option: TizenPaletteDisplayOption): boolean {
    try {
      webapis.palette.setDisplayOption(option)
      return true
    } catch (err: any) {
      this.logService.error(`[webapis.palette.setDisplayOption(${option})] [${err?.code}] ${err?.message}`)
      return false
    }
  }

  getDisplayOption(): string {
    try {
      return webapis.palette.getDisplayOption()
    } catch (err: any) {
      this.logService.error(`[webapis.palette.getDisplayOption()] [${err?.code}] ${err?.message}`)
      return 'Unsupported'
    }
  }

  setNightMode(activation: TizenPaletteNightMode): boolean {
    try {
      webapis.palette.setNightMode(activation)
      return true
    } catch (err: any) {
      this.logService.error(`[webapis.palette.setNightMode(${activation})] [${err?.code}] ${err?.message}`)
      return false
    }
  }

  getNightMode(): string {
    try {
      return webapis.palette.getNightMode()
    } catch (err: any) {
      this.logService.error(`[webapis.palette.getNightMode()] [${err?.code}] ${err?.message}`)
      return 'Unsupported'
    }
  }

  setMotionSleepTime(time: TizenPaletteMotionSleepTime): boolean {
    try {
      webapis.palette.setMotionSleepTime(time)
      return true
    } catch (err: any) {
      this.logService.error(`[webapis.palette.setMotionSleepTime(${time})] [${err?.code}] ${err?.message}`)
      return false
    }
  }

  getMotionSleepTime(): string {
    try {
      return webapis.palette.getMotionSleepTime()
    } catch (err: any) {
      this.logService.error(`[webapis.palette.getMotionSleepTime()] [${err?.code}] ${err?.message}`)
      return 'Unsupported'
    }
  }

  setMotionDetector(sensitivity: TizenPaletteMotionDetector): boolean {
    try {
      webapis.palette.setMotionDetector(sensitivity)
      return true
    } catch (err: any) {
      this.logService.error(`[webapis.palette.setMotionDetector(${sensitivity})] [${err?.code}] ${err?.message}`)
      return false
    }
  }

  getMotionDetector(): string {
    try {
      return webapis.palette.getMotionDetector()
    } catch (err: any) {
      this.logService.error(`[webapis.palette.getMotionDetector()] [${err?.code}] ${err?.message}`)
      return 'Unsupported'
    }
  }

  // Configure palette (no default so we can change it manually)
  async configure(paletteConfig: TizenPaletteConfig, mediasPath: string): Promise<void> {
    this.logService.debug(`[Tizen][Palette] config=${JSON.stringify(paletteConfig)}`)

    if (this.getVersion() === 'Unsupported') {
      this.logService.info('[Tizen][Palette] Palette is not supported on this TV, skipping configuration')
      return
    }

    if (!paletteConfig.enabled) {
      this.deleteAllImages()
      this.setPowerOffToPaletteMode({ mode: TizenPalettePowerOffMode.OFF })
      this.logCurrentSettings()
      return
    }

    const { playMode, scheduling, options } = paletteConfig
    this.setPlayMode(playMode)
    this.setSchedule({ enable: scheduling.enabled, onTime: scheduling.onTime, offTime: scheduling.offTime })
    this.setRotationTime(options.rotationTime)
    this.setDisplayOption(options.displayOption)
    this.setNightMode(options.nightMode)
    this.setMotionSleepTime(options.motionSleepTime)
    this.setMotionDetector(options.motionDetector)

    if (playMode === TizenPaletteConfigPlayMode.EXTERNAL) {
      try {
        await this.syncImages(paletteConfig.mediaIds, mediasPath)
      } catch (err: any) {
        this.logService.error(`[Tizen][Palette] Could not sync palette images, error = ${err?.message}`)
      }
      this.setPowerOffToPaletteMode({
        mode: TizenPalettePowerOffMode.ON,
        savedImageFolderPath: this.toAbsolutePath(this.PALETTE_IMAGES_PATH),
      })
    } else {
      this.deleteAllImages()
      this.setPowerOffToPaletteMode({ mode: TizenPalettePowerOffMode.ON })
    }

    this.logCurrentSettings()
  }

  private logCurrentSettings(): void {
    if (!this.logService.debugMode) {
      return
    }
    this.logService.debug(
      `[Tizen][Palette] current: version=${this.getVersion()}, powerOffToPaletteMode=${safeStringify(
        this.getPowerOffToPaletteMode()
      )}, playMode=${this.getPlayMode()}, schedule=${safeStringify(
        this.getSchedule()
      )}, rotationTime=${this.getRotationTime()}, displayOption=${this.getDisplayOption()}, nightMode=${this.getNightMode()}, motionSleepTime=${this.getMotionSleepTime()}, motionDetector=${this.getMotionDetector()}`
    )
  }

  private async syncImages(mediaIds: string[], mediasPath: string): Promise<void> {
    await this.ensureDirectory(this.PALETTE_IMAGES_PATH)
    const mediaFiles = (await this.listDirectory(mediasPath)).filter(filename =>
      mediaIds.includes(this.getId(filename))
    )
    const paletteFiles = await this.listDirectory(this.PALETTE_IMAGES_PATH)
    if (paletteFiles.length === mediaFiles.length && mediaFiles.every(filename => paletteFiles.includes(filename))) {
      this.logService.info(`[Tizen][Palette] images already synced (${mediaFiles.length}/${mediaIds.length} available)`)
      return
    }
    this.deleteAllImages()
    for (const filename of mediaFiles) {
      await this.copyFile(`${mediasPath}/${filename}`, `${this.PALETTE_IMAGES_PATH}/${filename}`)
    }
    this.logService.info(`[Tizen][Palette] images synced (${mediaFiles.length}/${mediaIds.length} available)`)
  }

  private getId(filename: string): string {
    return filename.substring(0, filename.lastIndexOf('.')) || filename
  }

  private toAbsolutePath(path: string): string {
    return tizen.filesystem.toURI(path).replace(/^file:\/\//, '')
  }

  private ensureDirectory(path: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (tizen.filesystem.pathExists(path)) {
        resolve()
      } else {
        tizen.filesystem.createDirectory(
          path,
          false,
          () => resolve(),
          (err: any) => reject(err)
        )
      }
    })
  }

  private listDirectory(path: string): Promise<string[]> {
    return new Promise((resolve, reject) => {
      tizen.filesystem.listDirectory(
        path,
        (files: string[]) => resolve(files),
        (err: any) => reject(err)
      )
    })
  }

  private copyFile(from: string, to: string): Promise<void> {
    return new Promise((resolve, reject) => {
      tizen.filesystem.copyFile(
        from,
        to,
        true,
        () => resolve(),
        (err: any) => reject(err)
      )
    })
  }
}
